import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { alpha, Box, Button, ButtonBase, Chip, ChipProps, Skeleton, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

export type ListItemCardProps = {
    loading?: boolean;
    /** Main visual rendered on the left (top on mobile), e.g. an image with an overlaid badge/flag */
    image?: React.ReactNode;
    /** Status chip rendered above the date/title, e.g. registration status */
    chip?: {
        label: React.ReactNode;
        color?: ChipProps['color'];
    };
    /** Label rendered above the title, e.g. a date or date range */
    dateLabel?: React.ReactNode;
    title?: React.ReactNode;
    /** Secondary line rendered below the title, e.g. an author/trainer credit */
    subtitle?: React.ReactNode;
    /** Row of metadata items rendered below the title, e.g. language, type, tags */
    metadataItems?: React.ReactNode[];
    /** Label for the trailing action button */
    actionLabel?: React.ReactNode;
    /** If provided, the whole card becomes a link to this route */
    linkTo?: string;
    onMouseEnter?: () => void;
    className?: string;
};

const ListItemCard = ({
    loading,
    image,
    chip,
    dateLabel,
    title,
    subtitle,
    metadataItems,
    actionLabel,
    linkTo,
    onMouseEnter,
    className,
}: ListItemCardProps) => {
    const CardContent = (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            bgcolor={(theme) => theme.palette.white.main}
            borderRadius="8px"
            border={1}
            borderColor={alpha('#000', 0.16)}
            px="20px"
            py="15px"
            spacing={{ xs: '10px', md: '19.5px' }}
            onMouseEnter={() => onMouseEnter?.()}
            height="100%"
            minHeight={{ xs: 'auto', md: 137 }}
            width="100%"
            className={className ? `group ${className}` : 'group'}
        >
            {loading ? (
                <Skeleton
                    variant="rounded"
                    sx={{
                        width: {
                            xs: '100%',
                            md: 164,
                        },
                        height: {
                            xs: 177,
                            md: 'auto',
                        },
                    }}
                />
            ) : (
                image && (
                    <Box
                        position="relative"
                        width={{
                            xs: '100%',
                            md: 164,
                        }}
                        flexShrink={0}
                        className="aspect-[calc(16/9)] md:aspect-auto transition-transform duration-300 ease-out-quad group-hover:scale-105"
                    >
                        {image}
                    </Box>
                )
            )}
            <Stack direction="column" spacing="8px" flexGrow={1} minWidth={0} overflow="hidden">
                {loading ? (
                    <>
                        <Skeleton variant="text" width="90%" height={26} />
                        <Skeleton variant="text" width="90%" height={16} />
                        <Skeleton variant="text" width="100%" height={19} />
                        <Skeleton variant="rounded" width="100%" height={24} />
                    </>
                ) : (
                    <>
                        {chip && (
                            <Chip
                                color={chip.color}
                                className="w-min"
                                label={
                                    <Typography fontSize={12} fontWeight={500} lineHeight="20px">
                                        {chip.label}
                                    </Typography>
                                }
                                classes={{ label: 'px-[10px] py-[3px]' }}
                            />
                        )}
                        {dateLabel && (
                            <Typography
                                fontSize={14}
                                color={alpha('#000', 0.4)}
                                fontWeight={500}
                                lineHeight="120%"
                                className="line-clamp-1"
                            >
                                {dateLabel}
                            </Typography>
                        )}
                        {title && (
                            <Typography fontSize={16} fontWeight={500} lineHeight="120%" className="line-clamp-2">
                                {title}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography
                                fontSize={14}
                                color={alpha('#000', 0.6)}
                                lineHeight="120%"
                                className="line-clamp-1"
                            >
                                {subtitle}
                            </Typography>
                        )}
                        {!!metadataItems?.length && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                flexWrap="wrap"
                                sx={{
                                    columnGap: '15px',
                                    rowGap: {
                                        xs: '10px',
                                        md: '5px',
                                    },
                                }}
                            >
                                {metadataItems}
                            </Stack>
                        )}
                    </>
                )}
            </Stack>
            {loading ? (
                <Skeleton
                    variant="rounded"
                    width={109}
                    height={20}
                    sx={{
                        alignSelf: {
                            xs: 'start',
                            md: 'center',
                        },
                    }}
                />
            ) : (
                actionLabel && (
                    <Button
                        variant="text"
                        endIcon={<ArrowForwardIcon />}
                        className="min-w-fit font-medium px-0 md:px-[6px]"
                        sx={{
                            alignSelf: {
                                xs: 'start',
                                md: 'center',
                            },
                        }}
                        classes={{
                            endIcon: 'transition-transform duration-300 ease-out-quad group-hover:translate-x-1',
                        }}
                    >
                        <Typography fontWeight={500} color="primary">
                            {actionLabel}
                        </Typography>
                    </Button>
                )
            )}
        </Stack>
    );

    if (loading || !linkTo) {
        return CardContent;
    }

    return (
        <ButtonBase component={Link} to={linkTo} focusRipple className="w-full h-full rounded-lg">
            {CardContent}
        </ButtonBase>
    );
};

export { ListItemCard };
